import { useState, useEffect, useRef } from 'react'
import {
  onAuthStateChanged,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
  GoogleAuthProvider,
  type User,
} from 'firebase/auth'
import { usePostHog } from '@posthog/react'
import * as Sentry from '@sentry/react'
import { auth } from './config'
import { identifyUser, resetUser } from './analytics'
import { acceptInvite, ensureUserContext, clearFirestoreCache, type UserContext } from './services/auth'
import Dashboard from './Dashboard'
import StaffConsole from './components/StaffConsole'
import './App.css'

const googleProvider = new GoogleAuthProvider()

/** Translate an invite-related HttpsError code into a user-facing message. */
function inviteErrorMessage(code: string): string {
  if (code === 'functions/permission-denied') {
    return 'This invite was sent to a different email address. Sign in with the correct email and try again.'
  }
  if (code === 'functions/not-found') {
    return 'This invite link is invalid or has already been used. Ask your admin for a new one.'
  }
  if (code === 'functions/deadline-exceeded') {
    return 'This invite link has expired. Ask your admin for a new one.'
  }
  return 'Could not accept the invite. Please try again or ask your admin for a new link.'
}

/** Maps Firebase auth error codes to human-readable messages */
function getAuthErrorMessage(code: string): string {
  const messages: Record<string, string> = {
    'auth/invalid-credential': 'Invalid email or password.',
    'auth/invalid-email': 'Please enter a valid email address.',
    'auth/user-not-found': 'No account found with this email.',
    'auth/wrong-password': 'Incorrect password.',
    'auth/email-already-in-use': 'This email is already registered. Sign in instead.',
    'auth/weak-password': 'Password must be at least 6 characters.',
    'auth/too-many-requests': 'Too many attempts. Please try again later.',
    'auth/popup-closed-by-user': 'Sign-in was cancelled.',
    'auth/cancelled-popup-request': 'Sign-in was cancelled.',
    'auth/unauthorized-domain': 'This domain is not authorized. Add localhost to Firebase Console → Authentication → Settings → Authorized domains.',
    'auth/popup-blocked': 'Sign-in popup was blocked. Allow popups for this site.',
    'auth/operation-not-allowed': 'Google sign-in is not enabled. Enable it in Firebase Console → Authentication → Sign-in method.',
    'auth/configuration-not-found': 'Google sign-in is not configured. In Firebase Console → Authentication → Sign-in method → Google, ensure the provider is enabled and Web SDK configuration is set up. You may need to add an OAuth client in Google Cloud Console.',
  }
  return messages[code] ?? `An error occurred (${code || 'unknown'}). Please try again.`
}

function App() {
  const [user, setUser] = useState<User | null>(null)
  const [userContext, setUserContext] = useState<UserContext | null>(null)
  const [authLoading, setAuthLoading] = useState(true)
  const [bootstrapError, setBootstrapError] = useState<string | null>(null)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  // Email/password account creation is offered only on an invite link: the form
  // defaults to "Create account" mode when the URL carries a token, and without
  // one the create/sign-in toggle below is not rendered at all. UI hygiene, not
  // the gate: Google sign-in still creates a Firebase Auth account on a first
  // sign-in, and the allowedEmails check in ensureUserContext is what refuses an
  // uninvited account.
  const [isRegistering, setIsRegistering] = useState(() => !!new URLSearchParams(window.location.search).get('token'))
  const [authError, setAuthError] = useState<string | null>(null)
  const [resetSent, setResetSent] = useState(false)
  const posthog = usePostHog()
  const isFirstAuthCheck = useRef(true)
  const prevUserRef = useRef<User | null>(null)
  // Read invite token from URL once at mount — stable ref, not state, so it's
  // available synchronously inside the onAuthStateChanged closure.
  // State holds the initial value (never updated); ref is the mutable copy for callbacks.
  const [initialToken] = useState(() => new URLSearchParams(window.location.search).get('token'))
  const pendingTokenRef = useRef(initialToken)
  const hasInviteToken = !!initialToken
  // Permalink deep-link to a saved eligibility check (`?check=<id>`). Read once
  // at mount; left in the URL (unlike the invite token) so a refresh re-opens
  // the same record. Dashboard consumes it after auth + org context resolve.
  const [initialCheckId] = useState(() => new URLSearchParams(window.location.search).get('check'))

  /**
   * Calls the ensureUserContext callable to set up org/clinic/claims,
   * then force-refreshes the ID token to pick up new custom claims.
   *
   * When an invite token is present in the URL, acceptInvite is called first
   * so the user doc is created in the correct org before ensureUserContext runs
   * (otherwise Case 1 would bootstrap a brand-new org for the invitee).
   */
  async function initUserContext(firebaseUser: User): Promise<UserContext> {
    const token = pendingTokenRef.current
    if (token) {
      try {
        await acceptInvite(token)
      } catch (err: unknown) {
        const code = err && typeof err === 'object' && 'code' in err
          ? String((err as { code: string }).code)
          : ''
        // already-exists → user already joined this org; proceed normally.
        // not-found → invite already used (or never valid). A returning user
        // landing on the invite URL with a stale token should still be able
        // to sign in — ensureUserContext below is the gate for valid accounts.
        if (code !== 'functions/already-exists' && code !== 'functions/not-found') {
          throw new Error(inviteErrorMessage(code))
        }
      }
      // Clear token from URL whether we succeeded or the user was already a member
      const url = new URL(window.location.href)
      url.searchParams.delete('token')
      window.history.replaceState({}, '', url.toString())
      pendingTokenRef.current = null
    }

    const ctx = await ensureUserContext()

    // Force token refresh to pick up new/updated custom claims (best-effort:
    // a transient network failure here is non-fatal — claims are refreshed on
    // the next successful auth round-trip).
    try {
      await firebaseUser.getIdToken(true)
    } catch {
      // swallow — proceed with the context that ensureUserContext already returned
    }

    return ctx
  }

  // Listen to Firebase auth state — resolves on page load and on login/logout
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      const wasLoggedOut = prevUserRef.current === null
      const isNowLoggedIn = firebaseUser !== null

      setUser(firebaseUser)

      if (firebaseUser) {
        identifyUser(firebaseUser.uid)
        try {
          const ctx = await initUserContext(firebaseUser)
          setUserContext(ctx)
          setBootstrapError(null)
        } catch (err) {
          Sentry.captureException(err)
          const code = err && typeof err === 'object' && 'code' in err
            ? String((err as { code: string }).code)
            : ''
          const message = code === 'auth/network-request-failed'
            ? 'Network error. Please check your connection and try again.'
            : err instanceof Error && err.message
              ? err.message
              : 'Failed to initialize your organization. Please try refreshing.'
          setBootstrapError(message)
        }
        // Only capture on actual login (null → user), not on session restoration
        if (!isFirstAuthCheck.current && wasLoggedOut && isNowLoggedIn) {
          posthog.capture('login_success')
        }
      } else {
        resetUser()
        setUserContext(null)
        setBootstrapError(null)
      }

      setAuthLoading(false)
      isFirstAuthCheck.current = false
      prevUserRef.current = firebaseUser
    })
    return unsubscribe
  }, [posthog])

  async function handleSignIn() {
    setAuthError(null)
    try {
      await signInWithPopup(auth, googleProvider)
    } catch (err: unknown) {
      const code = err && typeof err === 'object' && 'code' in err ? String((err as { code: string }).code) : ''
      setAuthError(getAuthErrorMessage(code))
    }
  }

  async function handleSignOut() {
    // Firestore enables IndexedDB persistence by default and caches docs
    // across sessions, with no per-user scoping. Without explicitly clearing
    // it on sign-out, the next user signing in on the same browser can briefly
    // see the prior user's cached docs via cache-first snapshots before the
    // server-side rule check denies the listener. Terminate + clear the cache
    // and reload so the next session starts with a fresh Firestore client.
    await signOut(auth)
    try {
      await clearFirestoreCache()
    } catch (err) {
      // Best-effort — `clearIndexedDbPersistence` rejects if other tabs hold
      // the DB, and `terminate` is idempotent-ish but can throw. Either way,
      // we still reload so in-memory state is gone for this tab.
      Sentry.captureException(err)
    }
    window.location.reload()
  }

  async function handleEmailAuth(e: React.FormEvent) {
    e.preventDefault()
    setAuthError(null)
    setResetSent(false)
    try {
      if (isRegistering) {
        await createUserWithEmailAndPassword(auth, email, password)
      } else {
        await signInWithEmailAndPassword(auth, email, password)
      }
    } catch (err: unknown) {
      const code = err && typeof err === 'object' && 'code' in err ? String((err as { code: string }).code) : ''
      setAuthError(getAuthErrorMessage(code))
    }
  }

  async function handleForgotPassword(e: React.MouseEvent) {
    e.preventDefault()
    setAuthError(null)
    if (!email.trim()) {
      setAuthError('Enter your email above, then click Forgot password.')
      return
    }
    try {
      await sendPasswordResetEmail(auth, email.trim())
      setResetSent(true)
    } catch (err: unknown) {
      const code = err && typeof err === 'object' && 'code' in err ? String((err as { code: string }).code) : ''
      setAuthError(getAuthErrorMessage(code))
    }
  }

  // Show nothing while Firebase resolves auth state (prevents flash)
  if (authLoading) {
    return <div className="auth-loading">Loading...</div>
  }

  // Not signed in — show login screen
  if (!user) {
    return (
      <div className="login-screen">
        <div className="login-card">
          <h1>VitalRev</h1>
          {hasInviteToken
            ? <p className="invite-banner">You've been invited — create an account or sign in to continue.</p>
            : <p className="subtitle">Sign in to continue</p>
          }
          <form className="login-form" onSubmit={handleEmailAuth}>
            <div className="input-group">
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                aria-label="Email"
              />
            </div>
            <div className="input-group">
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete={isRegistering ? 'new-password' : 'current-password'}
                aria-label="Password"
              />
            </div>
            <button type="submit" className="primary-button">
              {isRegistering ? 'Create account' : 'Sign in'}
            </button>
            <div className="login-form-links">
              {hasInviteToken && (
                <button
                  type="button"
                  className="link-button"
                  onClick={() => {
                    setIsRegistering(!isRegistering)
                    setAuthError(null)
                    setResetSent(false)
                  }}
                >
                  {isRegistering ? 'Already have an account?' : "Don't have an account?"}
                </button>
              )}
              {!isRegistering && (
                <button
                  type="button"
                  className="link-button"
                  onClick={handleForgotPassword}
                >
                  Forgot password?
                </button>
              )}
            </div>
            {authError && <p className="auth-error">{authError}</p>}
            {resetSent && <p className="auth-success">Reset email sent. Check your inbox.</p>}
          </form>
          <div className="login-divider">
            <span>or</span>
          </div>
          <button onClick={handleSignIn} className="google-button">
            <svg className="google-icon" viewBox="0 0 24 24" width="20" height="20" xmlns="http://www.w3.org/2000/svg">
              <g transform="matrix(1, 0, 0, 1, 27.009001, -39.238998)">
                <path fill="#4285F4" d="M -3.264 51.509 C -3.264 50.719 -3.334 49.969 -3.454 49.239 L -14.754 49.239 L -14.754 53.749 L -8.284 53.749 C -8.574 55.229 -9.424 56.479 -10.684 57.329 L -10.684 60.329 L -6.824 60.329 C -4.564 58.239 -3.264 55.159 -3.264 51.509 Z"/>
                <path fill="#34A853" d="M -14.754 63.239 C -11.514 63.239 -8.804 62.159 -6.824 60.329 L -10.684 57.329 C -11.764 58.049 -13.134 58.489 -14.754 58.489 C -17.884 58.489 -20.534 56.379 -21.484 53.529 L -25.464 53.529 L -25.464 56.619 C -23.494 60.539 -19.444 63.239 -14.754 63.239 Z"/>
                <path fill="#FBBC05" d="M -21.484 53.529 C -21.734 52.809 -21.864 52.039 -21.864 51.239 C -21.864 50.439 -21.734 49.669 -21.484 48.949 L -21.484 45.859 L -25.464 45.859 C -26.284 47.479 -26.754 49.299 -26.754 51.239 C -26.754 53.179 -26.284 54.999 -25.464 56.619 L -21.484 53.529 Z"/>
                <path fill="#EA4335" d="M -14.754 43.989 C -12.984 43.989 -11.404 44.599 -10.154 45.799 L -6.734 42.379 C -8.804 40.439 -11.514 39.239 -14.754 39.239 C -19.444 39.239 -23.494 41.939 -25.464 45.859 L -21.484 48.949 C -20.534 46.099 -17.884 43.989 -14.754 43.989 Z"/>
              </g>
            </svg>
            Sign in with Google
          </button>
        </div>
      </div>
    )
  }

  // Wait for user context to resolve before rendering dashboard
  if (bootstrapError) {
    return (
      <div className="auth-loading">
        <p className="auth-error">{bootstrapError}</p>
        <div className="bootstrap-error-actions">
          <button className="link-button" onClick={() => window.location.reload()}>Retry</button>
          <button className="link-button" onClick={handleSignOut}>Sign out</button>
        </div>
      </div>
    )
  }

  if (!userContext) {
    return <div className="auth-loading">Loading...</div>
  }

  // VitalRev staff, not currently viewing a tenant → staff console (org picker).
  if (userContext.staff && !userContext.impersonating) {
    return <StaffConsole user={user} onSignOut={handleSignOut} />
  }

  // Staff with no org and no active impersonation should never reach the
  // dashboard; the guard above covers it. Customers always have an orgId.
  if (!userContext.orgId) {
    return <div className="auth-loading">Loading...</div>
  }

  // After ending a view-as session, refresh the token (drops impersonation
  // claims) and reload back into the staff console.
  async function handleExitImpersonation() {
    try {
      await user!.getIdToken(true)
    } catch {
      // ignore — reload re-bootstraps from the server-side cleared claims
    }
    window.location.reload()
  }

  // Signed in — show dashboard (customer, or staff viewing a tenant read-only).
  return (
    <Dashboard
      user={user}
      orgId={userContext.orgId}
      role={userContext.role}
      clinicId={userContext.clinicId}
      isStaff={userContext.staff === true}
      onSignOut={handleSignOut}
      initialCheckId={initialCheckId}
      impersonation={userContext.impersonating ?? null}
      onReloadSession={handleExitImpersonation}
    />
  )
}

export default App
